import React, { useCallback, useEffect, useState } from "react";
import { Card, Button, Tabs, Collapse, Space, Tag, Typography, Row, Col, Popconfirm, Switch, Empty, Spin, Dropdown, Menu, Modal, Input, Select } from "antd";
import { createGlobalStyle } from "styled-components";
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    EnvironmentOutlined,
    PhoneOutlined,
    MailOutlined,
    UserOutlined,
    MoreOutlined,
    HolderOutlined,
    SearchOutlined
} from "@ant-design/icons";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import { useAppDispatch, useAppSelector } from "../../utils/hooks/redux";
import { selectEstablishmentType } from "../../redux/features/establishmentType/establishmentTypeSlice";
import { selectUserRole } from "../../redux/features/auth/selectors";
import { selectEstablishmentTypeQuestion } from "../../redux/features/establishmentTypeQuestion/establishmentTypeQuestionSlice";
import { selectEstablishmentLocation } from "../../redux/features/establishmentLocation/establishmentLocationSlice";
import { selectPlace } from "../../redux/features/place/placeSlice";
import { selectContact } from "../../redux/features/contact/contactSlice";
import {
    fetchEstablishmentType,
    deleteEstablishmentType,
    updateEstablishmentType,
    addEstablishmentType,
    updateEstablishmentOrder
} from "../../redux/features/establishmentType/establishmentAsyncThunk";
import {
    fetchEstablishmentTypeQuestions,
    deleteEstablishmentTypeQuestion,
    createEstablishmentTypeQuestion,
    updateEstablishmentTypeQuestion,
    reorderEstablishmentTypeQuestions
} from "../../redux/features/establishmentTypeQuestion/establishmentTypeQuestionAsyncThunk";
import {
    fetchEstablishmentLocations,
    deleteEstablishmentLocation,
    updateEstablishmentLocation,
    addEstablishmentLocation,
    getEstablishmentLocationClaimCount
} from "../../redux/features/establishmentLocation/establishmentLocationAsyncThunk";
import { fetchPlaces, addPlace, updatePlace, deletePlace } from "../../redux/features/place/placeAsyncThunk";
import { fetchContacts, addContact, updateContact, deleteContact } from "../../redux/features/contact/contactAsyncThunk";
import { ActivationStatus, EstablishmentType, EstablishmentLocation, Place, Contact } from "../../types";
import { EstablishmentTypes } from "../../types/QuestionTypes";
import { checkHasPermission } from "../../utils/helpers/checkHasPermission";
import { permissions } from "../../utils/constants/permissions";
import CreateEditCategoryModal from "./EstablishmentTypeModel";
import CreateEditQuestionModal from "./EstablishmentTypeQuestionModel";
import EstablishmentLocationModal from "./EstablishmentLocationModal";
import ContactModal from "./ContactModal";
import PlaceModal from "./PlaceModal";
import { Form } from "antd";

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Panel } = Collapse;

const GlobalDropdownStyles = createGlobalStyle`
    .ant-dropdown-menu-item.ant-dropdown-menu-item-danger:hover {
        color: #fff !important;
        background-color: #ff4d4f !important;
    }

    .ant-dropdown-menu-item.ant-dropdown-menu-item-danger:hover span {
        color: #fff !important;
    }

    .ant-dropdown-menu-item.ant-dropdown-menu-item-danger:hover .anticon {
        color: #fff !important;
    }

    /* Danger button hover - white text on red background */
    .ant-btn-text.ant-btn-dangerous:hover {
        color: #fff !important;
        background-color: #ff4d4f !important;
    }

    .ant-btn-text.ant-btn-dangerous:hover .anticon {
        color: #fff !important;
    }

    /* Vertically center collapse arrow icon */
    .ant-collapse-header {
        align-items: center !important;
    }

    .ant-collapse-expand-icon {
        align-self: center !important;
        padding-top: 0 !important;
    }
`;

const ImprovedEstablishmentTypePage: React.FC = () => {
    const dispatch = useAppDispatch();
    const { data: establishmentTypes, status: establishmentTypeStatus } = useAppSelector(selectEstablishmentType);
    const { data: questions } = useAppSelector(selectEstablishmentTypeQuestion);
    const { data: establishments, loading: establishmentsLoading } = useAppSelector(selectEstablishmentLocation);
    const { data: places, loading: placesLoading } = useAppSelector(selectPlace);
    const { data: contacts, loading: contactsLoading } = useAppSelector(selectContact);
    const userRole = useAppSelector(selectUserRole);

    // Helper function to get field type name
    const getFieldTypeName = (fieldType: number): string => {
        const typeEntry = Object.entries(EstablishmentTypes).find(([_, value]) => value === fieldType);
        return typeEntry ? typeEntry[0] : String(fieldType);
    };

    const [form] = Form.useForm();
    const [selectedEstablishmentType, setSelectedEstablishmentType] = useState<EstablishmentType | null>(null);
    const [editingEstablishmentType, setEditingEstablishmentType] = useState<EstablishmentType | null>(null);
    const [editingQuestion, setEditingQuestion] = useState<any | null>(null);
    const [editingEstablishment, setEditingEstablishment] = useState<EstablishmentLocation | null>(null);
    const [editingContact, setEditingContact] = useState<Contact | null>(null);
    const [editingPlace, setEditingPlace] = useState<Place | null>(null);
    const [currentEstablishmentId, setCurrentEstablishmentId] = useState<string | null>(null);
    const [currentPlaceId, setCurrentPlaceId] = useState<string | null>(null);

    const [establishmentTypeModalVisible, setEstablishmentTypeModalVisible] = useState(false);
    const [questionModalVisible, setQuestionModalVisible] = useState(false);
    const [establishmentModalVisible, setEstablishmentModalVisible] = useState(false);
    const [contactModalVisible, setContactModalVisible] = useState(false);
    const [placeModalVisible, setPlaceModalVisible] = useState(false);
    const [deleteConfirmModalVisible, setDeleteConfirmModalVisible] = useState(false);
    const [typeToDelete, setTypeToDelete] = useState<EstablishmentType | null>(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState("");

    // Establishment-location delete flow: when admin clicks Delete on an establishment
    // (Spirit Airlines etc.), we pre-check how many claims reference it. If any do, we
    // show "Disable instead" — preserving the name on existing claim cards.
    const [estDeleteTarget, setEstDeleteTarget] = useState<EstablishmentLocation | null>(null);
    const [estDeleteChecking, setEstDeleteChecking] = useState(false);
    const [estDeleteClaimCount, setEstDeleteClaimCount] = useState<number | null>(null);

    const [activeTab, setActiveTab] = useState<string>("establishments");
    const [specificTypeTab, setSpecificTypeTab] = useState<string>('all');
    const [establishmentSearchTerm, setEstablishmentSearchTerm] = useState<string>('');
    const [establishmentFilter, setEstablishmentFilter] = useState<string>('all');

    useEffect(() => {
        if (establishmentTypeStatus === "idle") {
            dispatch(fetchEstablishmentType());
        }
    }, [dispatch, establishmentTypeStatus]);

    useEffect(() => {
        if (selectedEstablishmentType) {
            dispatch(fetchEstablishmentTypeQuestions({
                search_query: "",
                offset: 0,
                limit: 100,
                establishmentTypeId: selectedEstablishmentType.id
            }));
            dispatch(fetchEstablishmentLocations({ establishmentTypeId: selectedEstablishmentType.id }));
            // Reset specific type tab when changing establishment type
            setSpecificTypeTab('all');
        }
    }, [selectedEstablishmentType, dispatch]);

    const hasPermission = useCallback(
        (permissionKey: string) => checkHasPermission(userRole, permissionKey),
        [userRole]
    );

    // Establishment Type handlers
    const parseNameTranslations = (record?: EstablishmentType | null) => {
        if (!record?.nameTranslations) {
            return {};
        }
        if (typeof record.nameTranslations === "string") {
            try {
                return JSON.parse(record.nameTranslations) || {};
            } catch (error) {
                console.warn("Failed to parse nameTranslations", error);
                return {};
            }
        }
        return record.nameTranslations;
    };

    const handleAddEstablishmentType = () => {
        setEditingEstablishmentType(null);
        form.resetFields();
        form.setFieldsValue({ nameTranslations: {}, enableLandingPage: false, heroContent: {} });
        setEstablishmentTypeModalVisible(true);
    };

    const handleEditEstablishmentType = (item: EstablishmentType) => {
        setEditingEstablishmentType(item);
        const normalizedTranslations = parseNameTranslations(item);
        form.setFieldsValue({ ...item, nameTranslations: normalizedTranslations });
        setEstablishmentTypeModalVisible(true);
    };

    const handleDeleteEstablishmentType = async (id: string) => {
        await dispatch(deleteEstablishmentType({ id }));
        if (selectedEstablishmentType?.id === id) {
            setSelectedEstablishmentType(null);
        }
    };

    const handleSaveEstablishmentType = async (values: any) => {
        const id = form.getFieldValue("id");
        try {
            console.log('DEBUG FRONTEND - Form values:', values);
            console.log('DEBUG FRONTEND - specificTypes:', values.specificTypes);

            let icon = null;
            const iconValues = await form.validateFields(["icon"]);
            if (iconValues?.icon) {
                const hasBlob = iconValues?.icon instanceof Blob;
                if (hasBlob) {
                    icon = iconValues?.icon;
                }
            }

            const formData = new FormData();
            formData.append('name', values.name);
            formData.append('status', String(ActivationStatus.Active));

            if (values.specificTypes && values.specificTypes.length > 0) {
                console.log('DEBUG FRONTEND - Stringified specificTypes:', JSON.stringify(values.specificTypes));
                formData.append('specificTypes', JSON.stringify(values.specificTypes));
            } else {
                console.log('DEBUG FRONTEND - No specificTypes to send');
            }

            if (values.translationKey) {
                formData.append("translationKey", values.translationKey);
            }

            if (values.nameTranslations) {
                const sanitizedTranslations = Object.entries(values.nameTranslations).reduce((acc, [lang, value]) => {
                    if (value && String(value).trim().length > 0) {
                        acc[lang] = String(value).trim();
                    }
                    return acc;
                }, {} as Record<string, string>);

                if (Object.keys(sanitizedTranslations).length > 0) {
                    formData.append("nameTranslations", JSON.stringify(sanitizedTranslations));
                }
            }

            // Add slug
            if (values.slug) {
                formData.append("slug", values.slug);
            }

            // Add heroContent only when enabled
            if (values.enableLandingPage && values.heroContent) {
                const heroContentData: any = { ...values.heroContent };
                const heroBlob = heroContentData.heroImageUrl instanceof Blob ? heroContentData.heroImageUrl : null;

                if (heroBlob) {
                    // Remove blob before serializing JSON
                    const { heroImageUrl, ...rest } = heroContentData;
                    formData.append('heroContent', JSON.stringify(rest));
                    formData.append('heroContent.heroImageUrl', heroBlob, (heroBlob as any).name || 'hero-image');
                } else {
                    formData.append('heroContent', JSON.stringify(heroContentData));
                }
            }
            if (values.enableLandingPage && values.heroContentTranslations) {
                formData.append('heroContentTranslations', JSON.stringify(values.heroContentTranslations));
            }
            if (values.availableLanguages && values.availableLanguages.length > 0) {
                formData.append('availableLanguages', JSON.stringify(Array.from(new Set([...(values.availableLanguages || []), 'en']))));
            }

            if (icon) {
                formData.append('icon', icon);
            } else if (id && editingEstablishmentType && 'fullUrl' in editingEstablishmentType && editingEstablishmentType.fullUrl) {
                formData.append('fullUrl', editingEstablishmentType.fullUrl);
            }

            if (id) {
                console.log('DEBUG FRONTEND - Updating establishment type with id:', id);
                await dispatch(updateEstablishmentType({ formData, id })).unwrap();
            } else {
                console.log('DEBUG FRONTEND - Creating new establishment type');
                await dispatch(addEstablishmentType({ formData })).unwrap();
            }
            console.log('DEBUG FRONTEND - Save successful, closing modal');
            form.resetFields();
            setEditingEstablishmentType(null);
            setEstablishmentTypeModalVisible(false);
        } catch (e) {
            console.error('DEBUG FRONTEND - Error during save:', e);
        }
    };

    const handleDeleteEstablishmentTypeClick = (type: EstablishmentType) => {
        setTypeToDelete(type);
        setDeleteConfirmText("");
        setDeleteConfirmModalVisible(true);
    };

    const handleConfirmDelete = async () => {
        if (deleteConfirmText === "DELETE" && typeToDelete) {
            await dispatch(deleteEstablishmentType({ id: typeToDelete.id }));
            setDeleteConfirmModalVisible(false);
            setTypeToDelete(null);
            setDeleteConfirmText("");
            if (selectedEstablishmentType?.id === typeToDelete.id) {
                setSelectedEstablishmentType(null);
            }
        }
    };

    const handleCancelDelete = () => {
        setDeleteConfirmModalVisible(false);
        setTypeToDelete(null);
        setDeleteConfirmText("");
    };

    const handleDragEnd = async (result: DropResult) => {
        if (!result.destination) return;

        const items = Array.from(establishmentTypes);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        // Create order updates with new indices
        const orderUpdates = items.map((item, index) => ({
            id: item.id,
            index: index
        }));

        try {
            await dispatch(updateEstablishmentOrder({ orderUpdates })).unwrap();
        } catch (error) {
            console.error("Failed to update order:", error);
        }
    };

    // Question handlers
    const handleAddQuestion = () => {
        if (!selectedEstablishmentType) return;
        setEditingQuestion(null);
        form.resetFields();
        form.setFieldsValue({
            establishmentType: selectedEstablishmentType.id
        });
        setQuestionModalVisible(true);
    };

    const handleEditQuestion = (item: any) => {
        console.log('=== EDIT QUESTION DEBUG ===');
        console.log('Item from backend:', item);
        console.log('questionType:', item.questionType);
        console.log('dynamicOptions:', item.dynamicOptions);

        setEditingQuestion(item);
        form.resetFields(); // Clear all previous form values

        const formValues = {
            ...item,
            establishmentType: item.establishmentType?.id || selectedEstablishmentType?.id,
        };

        // Explicitly set questionType if it exists
        if (item.questionType) {
            formValues.questionType = item.questionType;
        }

        console.log('Form values being set:', formValues);
        form.setFieldsValue(formValues);
        setQuestionModalVisible(true);
    };

    const handleDeleteQuestion = async (id: string) => {
        await dispatch(deleteEstablishmentTypeQuestion(id));
    };

    const handleSaveQuestion = async (values: any) => {
        if (editingQuestion) {
            await dispatch(updateEstablishmentTypeQuestion({ ...editingQuestion, ...values }));
        } else {
            await dispatch(createEstablishmentTypeQuestion(values));
        }
        setQuestionModalVisible(false);
    };

    const handleQuestionDragEnd = async (result: DropResult) => {
        if (!result.destination) return;

        const items = Array.from(questions);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        // Update order values
        const questionsWithNewOrder = items.map((question, index) => ({
            id: question.id,
            order: index
        }));

        // Optimistically update UI (you can dispatch a local action if needed)
        // Then call the reorder API
        await dispatch(reorderEstablishmentTypeQuestions({ questions: questionsWithNewOrder }));

        // Refresh the questions list
        if (selectedEstablishmentType) {
            await dispatch(fetchEstablishmentTypeQuestions({
                search_query: '',
                offset: 0,
                limit: 100,
                establishmentTypeId: selectedEstablishmentType.id
            }));
        }
    };

    // Establishment handlers
    const handleAddEstablishment = () => {
        if (!selectedEstablishmentType) return;
        setEditingEstablishment(null);
        setEstablishmentModalVisible(true);
    };

    const handleToggleEstablishmentStatus = async (establishment: EstablishmentLocation) => {
        const newStatus = establishment.status === ActivationStatus.Active
            ? ActivationStatus.Inactive
            : ActivationStatus.Active;

        const formData = new FormData();
        formData.append('status', String(newStatus));
        formData.append('name', establishment.name);
        formData.append('establishmentTypeId', establishment.establishmentTypeId);

        if (establishment.specificType) {
            formData.append('specificType', establishment.specificType);
        }

        if (establishment.establishmentTypes && establishment.establishmentTypes.length > 0) {
            formData.append('establishmentTypeIds', JSON.stringify(establishment.establishmentTypes.map(t => t.id)));
        }

        await dispatch(updateEstablishmentLocation({ id: establishment.id, formData }));
    };

    // Click "Delete" on an establishment → open the delete modal and fetch claim count.
    // Modal then branches: claimCount > 0 → "Disable instead"; claimCount === 0 → normal confirm.
    const openEstablishmentDelete = async (establishment: EstablishmentLocation) => {
        setEstDeleteTarget(establishment);
        setEstDeleteClaimCount(null);
        setEstDeleteChecking(true);
        try {
            const result = await dispatch(getEstablishmentLocationClaimCount({ id: establishment.id })).unwrap();
            setEstDeleteClaimCount(result.claimCount);
        } catch {
            // If the count check fails, leave claimCount null so the modal renders a
            // safe "can't verify" state and disables the destructive action.
            setEstDeleteClaimCount(null);
        } finally {
            setEstDeleteChecking(false);
        }
    };

    const closeEstablishmentDelete = () => {
        setEstDeleteTarget(null);
        setEstDeleteClaimCount(null);
        setEstDeleteChecking(false);
    };

    const confirmEstablishmentDelete = async () => {
        if (!estDeleteTarget) return;
        await dispatch(deleteEstablishmentLocation({ id: estDeleteTarget.id }));
        closeEstablishmentDelete();
    };

    const disableEstablishmentInstead = async () => {
        if (!estDeleteTarget) return;
        if (estDeleteTarget.status === ActivationStatus.Active) {
            await handleToggleEstablishmentStatus(estDeleteTarget);
        }
        closeEstablishmentDelete();
    };

    const handleSaveEstablishment = async (values: any) => {
        try {
            const formData = new FormData();

            // Required fields
            formData.append('name', values.name);
            formData.append('establishmentTypeId', values.establishmentTypeId);
            formData.append('status', String(values.status || ActivationStatus.Active));

            // Optional fields (convert to string to handle browser autofill)
            if (values.code) formData.append('code', String(values.code));
            if (values.specificType) formData.append('specificType', String(values.specificType));
            if (values.address) formData.append('address', String(values.address));
            if (values.city) formData.append('city', String(values.city));
            if (values.state) formData.append('state', String(values.state));
            if (values.country) formData.append('country', String(values.country));
            if (values.zipCode) formData.append('zipCode', String(values.zipCode));
            if (values.phone) formData.append('phone', String(values.phone));
            if (values.email) formData.append('email', String(values.email));

            // Handle multiple establishment types
            if (values.establishmentTypeIds && values.establishmentTypeIds.length > 0) {
                formData.append('establishmentTypeIds', JSON.stringify(values.establishmentTypeIds));
            }

            // Add slug (append even if empty to clear it)
            if (values.slug) {
                formData.append('slug', values.slug);
            }

            // Footer visibility
            formData.append('showInFooter', String(!!values.showInFooter));
            if (values.footerOrder != null) {
                formData.append('footerOrder', String(values.footerOrder));
            }

            // Add heroContent only when enabled
            if (values.enableLandingPage && values.heroContent) {
                const heroContentData: any = { ...values.heroContent };
                const imageFields = ['heroImageUrl'];
                const extractedBlobs: Record<string, Blob> = {};

                for (const field of imageFields) {
                    if (heroContentData[field] instanceof Blob) {
                        extractedBlobs[field] = heroContentData[field];
                        delete heroContentData[field];
                    }
                }

                formData.append('heroContent', JSON.stringify(heroContentData));

                for (const [field, blob] of Object.entries(extractedBlobs)) {
                    formData.append(`heroContent.${field}`, blob, (blob as any).name || field);
                }
            }
            if (values.enableLandingPage && values.heroContentTranslations) {
                formData.append('heroContentTranslations', JSON.stringify(values.heroContentTranslations));
            }
            // Per-location TimeNot CMS override (independent of enableLandingPage so it can
            // be edited even when no full landing page is enabled).
            if (values.timeNotContent && typeof values.timeNotContent === 'object') {
                const timeNotData: any = { ...values.timeNotContent };
                let timeNotImageBlob: Blob | null = null;
                if (timeNotData.imageUrl instanceof Blob) {
                    timeNotImageBlob = timeNotData.imageUrl;
                    delete timeNotData.imageUrl;
                }
                formData.append('timeNotContent', JSON.stringify(timeNotData));
                if (timeNotImageBlob) {
                    formData.append('timeNotContent.imageUrl', timeNotImageBlob, (timeNotImageBlob as any).name || 'timeNotImage');
                }
            }
            if (values.timeNotContentTranslations && typeof values.timeNotContentTranslations === 'object') {
                formData.append('timeNotContentTranslations', JSON.stringify(values.timeNotContentTranslations));
            }
            if (values.availableLanguages && values.availableLanguages.length > 0) {
                formData.append('availableLanguages', JSON.stringify(Array.from(new Set([...(values.availableLanguages || []), 'en']))));
            }

            // Always send pricingPlanIds, even when empty — empty means "no scoping, use all
            // active plans" and the backend needs to see [] (not an omitted field) to clear
            // an existing whitelist.
            formData.append(
                'pricingPlanIds',
                JSON.stringify(Array.isArray(values.pricingPlanIds) ? values.pricingPlanIds : [])
            );

            // Handle icon file upload
            if (values.icon && values.icon.length > 0) {
                const iconFile = values.icon[0];
                if (iconFile.originFileObj) {
                    formData.append('icon', iconFile.originFileObj);
                }
            }

            if (values.id) {
                // Update existing establishment
                await dispatch(updateEstablishmentLocation({ id: values.id, formData })).unwrap();
            } else {
                // Create new establishment
                await dispatch(addEstablishmentLocation({ formData })).unwrap();
            }

            setEstablishmentModalVisible(false);
            setEditingEstablishment(null);
        } catch (error) {
            console.error('Error saving establishment:', error);
        }
    };

    const loadEstablishmentDetails = async (establishmentId: string) => {
        await dispatch(fetchPlaces({ establishmentLocationId: establishmentId, status: 1 }));
        await dispatch(fetchContacts({ establishmentLocationId: establishmentId }));
    };

    // Contact handlers
    const handleAddContact = (establishmentId: string, placeId?: string) => {
        setCurrentEstablishmentId(establishmentId);
        setCurrentPlaceId(placeId || null);
        setEditingContact(null);
        setContactModalVisible(true);
    };

    const handleSaveContact = async (values: any) => {
        try {
            const contactData: any = {
                name: values.name,
                title: values.title || '',
                department: values.department || '',
                phone: values.phone || '',
                email: values.email || '',
                mobilePhone: values.mobilePhone || '',
                fax: values.fax || '',
                address: values.address || '',
                city: values.city || '',
                state: values.state || '',
                country: values.country || '',
                zipCode: values.zipCode || '',
                website: values.website || '',
                lostAndFoundFormLink: values.lostAndFoundFormLink || '',
                notes: values.notes || '',
                isPrimary: values.isPrimary || false,
                status: ActivationStatus.Active,
            };

            if (currentEstablishmentId) {
                contactData.establishmentLocationId = currentEstablishmentId;
            }
            if (currentPlaceId) {
                contactData.placeId = currentPlaceId;
            }

            if (editingContact) {
                await dispatch(updateContact({ id: editingContact.id, data: contactData })).unwrap();
            } else {
                await dispatch(addContact(contactData)).unwrap();
            }

            setContactModalVisible(false);
            setEditingContact(null);
            if (currentEstablishmentId) {
                await dispatch(fetchContacts({ establishmentLocationId: currentEstablishmentId }));
            }
        } catch (error) {
            console.error('Error saving contact:', error);
        }
    };

    // Place handlers
    const handleAddPlace = (establishmentId: string) => {
        setCurrentEstablishmentId(establishmentId);
        setEditingPlace(null);
        setPlaceModalVisible(true);
    };

    const handleEditPlace = (place: Place) => {
        setEditingPlace(place);
        setCurrentEstablishmentId(place.establishmentLocationId);
        setPlaceModalVisible(true);
    };

    const handleDeletePlace = async (placeId: string, establishmentId: string) => {
        try {
            await dispatch(deletePlace({ id: placeId })).unwrap();
            await dispatch(fetchPlaces({ establishmentLocationId: establishmentId, status: 1 }));
        } catch (error) {
            console.error('Error deleting place:', error);
        }
    };

    const handleSavePlace = async (values: any) => {
        try {
            const formData = new FormData();
            formData.append('name', values.name);
            formData.append('status', String(ActivationStatus.Active));

            if (currentEstablishmentId) {
                formData.append('establishmentLocationId', currentEstablishmentId);
            }

            // Handle icon file upload
            if (values.icon && values.icon.length > 0) {
                const iconFile = values.icon[0];
                if (iconFile.originFileObj) {
                    formData.append('icon', iconFile.originFileObj);
                }
            }

            if (editingPlace) {
                await dispatch(updatePlace({ id: editingPlace.id, formData })).unwrap();
            } else {
                await dispatch(addPlace({ formData })).unwrap();
            }

            setPlaceModalVisible(false);
            setEditingPlace(null);
            if (currentEstablishmentId) {
                await dispatch(fetchPlaces({ establishmentLocationId: currentEstablishmentId, status: 1 }));
            }
        } catch (error) {
            console.error('Error saving place:', error);
        }
    };

    const getEstablishmentPlaces = (establishmentId: string) => {
        return places.filter(p => p.establishmentLocationId === establishmentId);
    };

    const getEstablishmentContacts = (establishmentId: string) => {
        return contacts.filter(c => c.establishmentLocationId === establishmentId && !c.placeId);
    };

    const getPlaceContacts = (placeId: string) => {
        return contacts.filter(c => c.placeId === placeId);
    };

    const handleEditContact = (contact: Contact) => {
        setEditingContact(contact);
        setCurrentEstablishmentId(contact.establishmentLocationId || null);
        setCurrentPlaceId(contact.placeId || null);
        setContactModalVisible(true);
    };

    const handleDeleteContact = async (contactId: string, establishmentId?: string) => {
        try {
            await dispatch(deleteContact({ id: contactId })).unwrap();
            if (establishmentId) {
                await dispatch(fetchContacts({ establishmentLocationId: establishmentId }));
            }
        } catch (error) {
            console.error('Error deleting contact:', error);
        }
    };

    const renderContactCard = (contact: Contact) => (
        <Card
            size="small"
            style={{ marginBottom: 8 }}
            extra={
                <Space size="small">
                    <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEditContact(contact)}
                    />
                    <Popconfirm
                        title="Delete Contact"
                        description="Are you sure you want to delete this contact?"
                        onConfirm={() => handleDeleteContact(contact.id, contact.establishmentLocationId)}
                        okText="Yes"
                        cancelText="No"
                        okButtonProps={{ danger: true }}
                    >
                        <Button
                            type="text"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                        />
                    </Popconfirm>
                </Space>
            }
        >
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div>
                    <UserOutlined style={{ marginRight: 8 }} />
                    <Text strong>{contact.name}</Text>
                    {contact.isPrimary && <Tag color="gold" style={{ marginLeft: 8 }}>Primary</Tag>}
                    {contact.title && <Text type="secondary" style={{ marginLeft: 8 }}>({contact.title})</Text>}
                </div>
                {contact.email && (
                    <div>
                        <MailOutlined style={{ marginRight: 8 }} />
                        <Text copyable>{contact.email}</Text>
                    </div>
                )}
                {contact.phone && (
                    <div>
                        <PhoneOutlined style={{ marginRight: 8 }} />
                        <Text copyable>{contact.phone}</Text>
                    </div>
                )}
            </Space>
        </Card>
    );

    const renderEstablishmentAccordion = () => {
        // Filter by establishment type
        let filteredEstablishments = establishments.filter(
            e => e.establishmentTypeId === selectedEstablishmentType?.id ||
                 e.establishmentTypes?.some(t => t.id === selectedEstablishmentType?.id)
        );

        // Filter by search term
        if (establishmentSearchTerm) {
            const searchLower = establishmentSearchTerm.toLowerCase();
            filteredEstablishments = filteredEstablishments.filter(e =>
                e.name.toLowerCase().includes(searchLower) ||
                e.code?.toLowerCase().includes(searchLower) ||
                e.specificType?.toLowerCase().includes(searchLower)
            );
        }

        // Filter by landing page / footer status
        if (establishmentFilter === 'landing') {
            filteredEstablishments = filteredEstablishments.filter(e => !!e.slug);
        } else if (establishmentFilter === 'footer') {
            filteredEstablishments = filteredEstablishments.filter(e => e.showInFooter === true);
        } else if (establishmentFilter === 'both') {
            filteredEstablishments = filteredEstablishments.filter(e => !!e.slug && e.showInFooter === true);
        }

        if (filteredEstablishments.length === 0 && !establishmentSearchTerm && establishmentFilter === 'all') {
            return (
                <Empty
                    description="No establishments yet"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                >
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAddEstablishment}>
                        Add First Establishment
                    </Button>
                </Empty>
            );
        }

        // Get unique specific types from the filtered establishments
        const specificTypes: string[] = Array.from(
            new Set(
                filteredEstablishments
                    .map(e => e.specificType)
                    .filter((type): type is string => Boolean(type))
            )
        ).sort();

        // Filter establishments by active specific type tab
        const tabFilteredEstablishments = specificTypeTab === 'all'
            ? filteredEstablishments
            : filteredEstablishments.filter(e => e.specificType === specificTypeTab);

        const renderEstablishmentsList = (establishments: typeof filteredEstablishments) => (
            <Collapse
                accordion
                size="small"
                onChange={(key) => {
                    if (key && typeof key === 'string') {
                        loadEstablishmentDetails(key);
                    } else if (Array.isArray(key) && key.length > 0) {
                        loadEstablishmentDetails(key[0]);
                    }
                }}
            >
                {establishments.map(establishment => {
                    const establishmentPlaces = getEstablishmentPlaces(establishment.id);
                    const establishmentContacts = getEstablishmentContacts(establishment.id);

                    return (
                        <Panel
                            key={establishment.id}
                            header={
                                <Row justify="space-between" align="middle" style={{ width: '100%' }}>
                                    <Col flex="auto">
                                        <Space size="small">
                                            {establishment.icon && (
                                                <img src={establishment.fullUrl} alt={establishment.name} style={{ width: 20, height: 20, objectFit: 'contain' }} />
                                            )}
                                            <Text strong style={{ fontSize: '13px' }}>
                                                {establishment.name}
                                                {establishment.code && ` - ${establishment.code}`}
                                            </Text>
                                        </Space>
                                    </Col>
                                    <Col>
                                        <Space onClick={(e) => e.stopPropagation()} size="small">
                                            <Switch
                                                checked={establishment.status === ActivationStatus.Active}
                                                onChange={() => handleToggleEstablishmentStatus(establishment)}
                                                size="small"
                                            />
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<EditOutlined />}
                                                onClick={() => {
                                                    setEditingEstablishment(establishment);
                                                    setEstablishmentModalVisible(true);
                                                }}
                                            />
                                            <Dropdown
                                                overlay={
                                                    <Menu>
                                                        <Menu.Item
                                                            key="delete"
                                                            danger
                                                            icon={<DeleteOutlined />}
                                                            onClick={(e) => {
                                                                e.domEvent.stopPropagation();
                                                                openEstablishmentDelete(establishment);
                                                            }}
                                                        >
                                                            Delete Establishment
                                                        </Menu.Item>
                                                    </Menu>
                                                }
                                                trigger={['click']}
                                            >
                                                <Button type="text" size="small" icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
                                            </Dropdown>
                                        </Space>
                                    </Col>
                                </Row>
                            }
                        >
                            <Row gutter={[16, 16]}>
                                {/* Establishment Info */}
                                <Col span={24}>
                                    <Card size="small" title="📍 Address Information">
                                        {establishment.address && (
                                            <div>
                                                <EnvironmentOutlined style={{ marginRight: 8 }} />
                                                <Text>{establishment.address}</Text>
                                                {establishment.city && <Text>, {establishment.city}</Text>}
                                                {establishment.state && <Text>, {establishment.state}</Text>}
                                                {establishment.zipCode && <Text> {establishment.zipCode}</Text>}
                                            </div>
                                        )}
                                        {establishment.phone && (
                                            <div style={{ marginTop: 8 }}>
                                                <PhoneOutlined style={{ marginRight: 8 }} />
                                                <Text copyable>{establishment.phone}</Text>
                                            </div>
                                        )}
                                        {establishment.email && (
                                            <div style={{ marginTop: 8 }}>
                                                <MailOutlined style={{ marginRight: 8 }} />
                                                <Text copyable>{establishment.email}</Text>
                                            </div>
                                        )}
                                    </Card>
                                </Col>

                                {/* Establishment Contacts */}
                                <Col xs={24} lg={12}>
                                    <Card
                                        size="small"
                                        title={`📇 Establishment Contacts (${establishmentContacts.length}/10)`}
                                        extra={
                                            <Button
                                                type="link"
                                                size="small"
                                                icon={<PlusOutlined />}
                                                disabled={establishmentContacts.length >= 10}
                                                onClick={() => handleAddContact(establishment.id)}
                                            >
                                                Add Contact
                                            </Button>
                                        }
                                    >
                                        {establishmentContacts.length === 0 ? (
                                            <Empty description="No contacts" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                        ) : (
                                            establishmentContacts.map(contact => renderContactCard(contact))
                                        )}
                                    </Card>
                                </Col>

                                {/* Places */}
                                <Col xs={24} lg={12}>
                                    <Card
                                        size="small"
                                        title={`📍 Places (${establishmentPlaces.length})`}
                                        extra={
                                            <Button
                                                type="link"
                                                size="small"
                                                icon={<PlusOutlined />}
                                                onClick={() => handleAddPlace(establishment.id)}
                                            >
                                                Add Place
                                            </Button>
                                        }
                                    >
                                        {establishmentPlaces.length === 0 ? (
                                            <Empty description="No places" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                        ) : (
                                            <Collapse size="small">
                                                {establishmentPlaces.map(place => {
                                                    const placeContacts = getPlaceContacts(place.id);
                                                    return (
                                                        <Panel
                                                            key={place.id}
                                                            header={
                                                                <Row justify="space-between" align="middle" style={{ width: '100%' }}>
                                                                    <Col>
                                                                        <Space>
                                                                            <Text strong>{place.name}</Text>
                                                                            <Tag>📇 {placeContacts.length}/10</Tag>
                                                                        </Space>
                                                                    </Col>
                                                                    <Col>
                                                                        <Space size="small" onClick={(e) => e.stopPropagation()}>
                                                                            <Button
                                                                                type="text"
                                                                                size="small"
                                                                                icon={<EditOutlined />}
                                                                                onClick={() => handleEditPlace(place)}
                                                                            />
                                                                            <Popconfirm
                                                                                title="Delete Place"
                                                                                description="This will also delete all contacts in this place. Continue?"
                                                                                onConfirm={() => handleDeletePlace(place.id, establishment.id)}
                                                                                okText="Yes"
                                                                                cancelText="No"
                                                                                okButtonProps={{ danger: true }}
                                                                            >
                                                                                <Button
                                                                                    type="text"
                                                                                    size="small"
                                                                                    danger
                                                                                    icon={<DeleteOutlined />}
                                                                                />
                                                                            </Popconfirm>
                                                                            <Button
                                                                                type="link"
                                                                                size="small"
                                                                                icon={<PlusOutlined />}
                                                                                disabled={placeContacts.length >= 10}
                                                                                onClick={() => handleAddContact(establishment.id, place.id)}
                                                                            >
                                                                                Add Contact
                                                                            </Button>
                                                                        </Space>
                                                                    </Col>
                                                                </Row>
                                                            }
                                                            extra={null}
                                                        >
                                                            {placeContacts.length === 0 ? (
                                                                <Empty description="No contacts" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                                            ) : (
                                                                placeContacts.map(contact => renderContactCard(contact))
                                                            )}
                                                        </Panel>
                                                    );
                                                })}
                                            </Collapse>
                                        )}
                                    </Card>
                                </Col>
                            </Row>
                        </Panel>
                    );
                })}
            </Collapse>
        );

        // Show empty state when search has no results
        if (filteredEstablishments.length === 0 && establishmentSearchTerm) {
            return (
                <Empty
                    description={`No establishments found matching "${establishmentSearchTerm}"`}
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
            );
        }

        // If there are specific types, show tabs, otherwise just show the list
        if (specificTypes.length > 0) {
            const tabItems = specificTypes.map(type => {
                const typeEstablishments = filteredEstablishments.filter(e => e.specificType === type);
                const count = typeEstablishments.length;
                return {
                    key: type,
                    label: `${type} (${count})`,
                    children: typeEstablishments.length > 0
                        ? renderEstablishmentsList(typeEstablishments)
                        : <Empty description="No establishments in this category" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                };
            });

            return (
                <Tabs
                    activeKey={specificTypeTab}
                    onChange={setSpecificTypeTab}
                    size="small"
                    items={tabItems}
                    style={{ marginTop: 16 }}
                />
            );
        }

        return renderEstablishmentsList(filteredEstablishments);
    };

    return (
        <div style={{ padding: 24 }}>
            <GlobalDropdownStyles />
            {/* Header */}
            <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
                <Col>
                    {/* <Title level={3}>Establishment Type Management</Title> */}
                </Col>
                <Col>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAddEstablishmentType}
                        disabled={!hasPermission(permissions.AddEditEstablishmentType)}
                    >
                        Add Establishment Type
                    </Button>
                </Col>
            </Row>

            {/* Establishment Types List */}
            <Card title="Select Establishment Type" style={{ marginBottom: 16 }}>
                {/* <Title level={5}>Select Establishment Type</Title> */}
                {establishmentTypeStatus === "loading" ? (
                    <Spin />
                ) : (
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="establishment-types" direction="horizontal">
                            {(provided) => (
                                <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                    style={{ display: 'flex', flexWrap: 'nowrap', gap: '16px', overflowX: 'auto', padding: '16px' }}
                                >
                                    {establishmentTypes.map((type, index) => (
                                        <Draggable key={type.id} draggableId={type.id} index={index}>
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    style={{
                                                        ...provided.draggableProps.style,
                                                        opacity: snapshot.isDragging ? 0.8 : 1,
                                                        flex: '1 1 0',
                                                        minWidth: '180px'
                                                    }}
                                                >
                                                    <Card
                                                        size="small"
                                                        hoverable
                                                        onClick={() => setSelectedEstablishmentType(type)}
                                                        style={{
                                                            borderColor: selectedEstablishmentType?.id === type.id ? '#1890ff' : undefined,
                                                            borderWidth: selectedEstablishmentType?.id === type.id ? 2 : 1,
                                                            cursor: snapshot.isDragging ? 'grabbing' : 'pointer',
                                                            height: '100%'
                                                        }}
                            >
                                <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ position: 'absolute', top: 4, right: 4, zIndex: 10 }}>
                                        <Dropdown
                                            menu={{
                                                items: [
                                                    {
                                                        key: 'edit',
                                                        label: 'Edit',
                                                        icon: <EditOutlined />,
                                                        onClick: (e) => {
                                                            e.domEvent.stopPropagation();
                                                            handleEditEstablishmentType(type);
                                                        }
                                                    },
                                                    {
                                                        key: 'delete',
                                                        danger: true,
                                                        label: 'Delete',
                                                        icon: <DeleteOutlined />,
                                                        onClick: (e) => {
                                                            e.domEvent.stopPropagation();
                                                            handleDeleteEstablishmentTypeClick(type);
                                                        }
                                                    }
                                                ]
                                            }}
                                            trigger={['click']}
                                        >
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<MoreOutlined />}
                                                onClick={(e) => e.stopPropagation()}
                                                style={{ color: '#8c8c8c' }}
                                            />
                                        </Dropdown>
                                    </div>
                                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 12px 12px' }}>
                                        <Space direction="vertical" align="center" size="middle" style={{ width: '100%' }}>
                                            {type.icon && <img src={type.fullUrl} alt={type.name} style={{ width: 32, height: 32, objectFit: 'contain' }} />}
                                            <Text strong style={{ textAlign: 'center', width: '100%' }}>{type.name}</Text>
                                            {type.specificTypes && type.specificTypes.length > 0 && (
                                                <div style={{
                                                    display: 'flex',
                                                    flexWrap: 'nowrap',
                                                    gap: '4px',
                                                    justifyContent: 'center',
                                                    overflow: 'hidden',
                                                    width: '100%'
                                                }}>
                                                    {type.specificTypes.map((st, idx) => {
                                                        // Handle both old string format and new object format
                                                        const typeName = typeof st === 'string' ? st : st.name;
                                                        const isQuestionType = typeof st === 'object' && st.isQuestionType;
                                                        return (
                                                            <Tag
                                                                key={idx}
                                                                color={isQuestionType ? "purple" : "blue"}
                                                                style={{
                                                                    fontSize: '11px',
                                                                    padding: '0 4px',
                                                                    margin: 0,
                                                                    lineHeight: '18px'
                                                                }}
                                                            >
                                                                {typeName}
                                                                {isQuestionType && " ★"}
                                                            </Tag>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </Space>
                                    </div>
                                    <div
                                        {...provided.dragHandleProps}
                                        style={{
                                            cursor: 'grab',
                                            padding: '8px',
                                            fontSize: '16px',
                                            color: '#8c8c8c',
                                            textAlign: 'center',
                                            borderTop: '1px solid #f0f0f0'
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <HolderOutlined />
                                    </div>
                                </div>
                                                    </Card>
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                )}
            </Card>

            {/* Content Tabs */}
            {selectedEstablishmentType && (
                <Card title={`Managing: ${selectedEstablishmentType.name}`}>
                    <Tabs activeKey={activeTab} onChange={setActiveTab}>
                        <TabPane tab="🏢 Establishments" key="establishments">
                            <Row gutter={16} style={{ marginBottom: 16 }} align="middle">
                                <Col flex="auto">
                                    <Input
                                        placeholder="Search establishments by name, code, or type..."
                                        prefix={<SearchOutlined />}
                                        value={establishmentSearchTerm}
                                        onChange={(e) => setEstablishmentSearchTerm(e.target.value)}
                                        allowClear
                                    />
                                </Col>
                                <Col>
                                    <Select
                                        value={establishmentFilter}
                                        onChange={(val) => setEstablishmentFilter(val)}
                                        style={{ width: 200 }}
                                        options={[
                                            { value: 'all', label: 'All' },
                                            { value: 'landing', label: 'Landing Page Enabled' },
                                            { value: 'footer', label: 'Show in Footer' },
                                            { value: 'both', label: 'Both Enabled' },
                                        ]}
                                    />
                                </Col>
                                <Col>
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={handleAddEstablishment}
                                    >
                                        Add Establishment
                                    </Button>
                                </Col>
                            </Row>
                            {establishmentsLoading ? <Spin /> : renderEstablishmentAccordion()}
                        </TabPane>

                        <TabPane tab="❓ Questions" key="questions">
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Button type="primary" icon={<PlusOutlined />} onClick={handleAddQuestion}>
                                    Add Question
                                </Button>
                                {questions.length === 0 ? (
                                    <Empty description="No questions yet" />
                                ) : (
                                    <DragDropContext onDragEnd={handleQuestionDragEnd}>
                                        <Droppable droppableId="questions-list">
                                            {(provided) => (
                                                <Card
                                                    {...provided.droppableProps}
                                                    ref={provided.innerRef}
                                                >
                                                    {questions.map((q: any, index: number) => (
                                                        <Draggable key={q.id} draggableId={q.id} index={index}>
                                                            {(provided, snapshot) => (
                                                                <div
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    style={{
                                                                        marginBottom: 6,
                                                                        padding: '8px 12px',
                                                                        backgroundColor: snapshot.isDragging ? '#e6f7ff' : '#fafafa',
                                                                        border: '1px solid #d9d9d9',
                                                                        borderRadius: 4,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'space-between',
                                                                        ...provided.draggableProps.style
                                                                    }}
                                                                >
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                        <div {...provided.dragHandleProps} style={{ display: 'flex', alignItems: 'center' }}>
                                                                            <HolderOutlined style={{ fontSize: 12, color: '#999', cursor: 'grab' }} />
                                                                        </div>
                                                                        <Text strong style={{ fontSize: 13 }}>{q.fieldName}</Text>
                                                                        {q.questionType && <Tag color="purple" style={{ margin: 0, fontSize: 11, padding: '0 6px', lineHeight: '20px' }}>{q.questionType}</Tag>}
                                                                        <Tag color="blue" style={{ margin: 0, fontSize: 11, padding: '0 6px', lineHeight: '20px' }}>{getFieldTypeName(q.fieldType)}</Tag>
                                                                        {q.isRequired && <Tag color="red" style={{ margin: 0, fontSize: 11, padding: '0 6px', lineHeight: '20px' }}>Required</Tag>}
                                                                    </div>
                                                                    <Space size={4}>
                                                                        <Button
                                                                            type="text"
                                                                            size="small"
                                                                            icon={<EditOutlined />}
                                                                            onClick={() => handleEditQuestion(q)}
                                                                            style={{ padding: '2px 6px' }}
                                                                        />
                                                                        <Popconfirm
                                                                            title="Delete this question?"
                                                                            onConfirm={() => handleDeleteQuestion(q.id)}
                                                                        >
                                                                            <Button type="text" size="small" icon={<DeleteOutlined />} danger style={{ padding: '2px 6px' }} />
                                                                        </Popconfirm>
                                                                    </Space>
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    ))}
                                                    {provided.placeholder}
                                                </Card>
                                            )}
                                        </Droppable>
                                    </DragDropContext>
                                )}
                            </Space>
                        </TabPane>
                    </Tabs>
                </Card>
            )}

            {/* Modals */}
            <CreateEditCategoryModal
                isModalOpen={establishmentTypeModalVisible}
                handleSave={handleSaveEstablishmentType}
                onCancel={() => {
                    setEstablishmentTypeModalVisible(false);
                    setEditingEstablishmentType(null);
                    form.resetFields();
                }}
                form={form}
                editingRecord={editingEstablishmentType}
                setEditingRecord={setEditingEstablishmentType}
                hasAddPermission={hasPermission(permissions.AddEditEstablishmentType)}
                hasEditPermission={hasPermission(permissions.AddEditEstablishmentType)}
                hasViewPermission={hasPermission(permissions.ViewEstablishmentType)}
            />

            <CreateEditQuestionModal
                isModalOpen={questionModalVisible}
                handleSave={handleSaveQuestion}
                onCancel={() => {
                    setQuestionModalVisible(false);
                    setEditingQuestion(null);
                    form.resetFields();
                }}
                form={form}
                selectedEstablishmentTypeId={selectedEstablishmentType?.id}
                hasAddPermission={hasPermission(permissions.AddEditEstablishmentTypeQuestion)}
                hasEditPermission={hasPermission(permissions.AddEditEstablishmentTypeQuestion)}
                hasViewPermission={hasPermission(permissions.ViewEstablishmentTypeQuestion)}
            />

            {selectedEstablishmentType && (
                <EstablishmentLocationModal
                    visible={establishmentModalVisible}
                    onCancel={() => {
                        setEstablishmentModalVisible(false);
                        setEditingEstablishment(null);
                    }}
                    onSave={handleSaveEstablishment}
                    editingRecord={editingEstablishment}
                    establishmentTypeId={selectedEstablishmentType.id}
                    loading={establishmentsLoading}
                />
            )}

            {/* Contact Modal */}
            <ContactModal
                visible={contactModalVisible}
                onCancel={() => {
                    setContactModalVisible(false);
                    setEditingContact(null);
                    setCurrentEstablishmentId(null);
                    setCurrentPlaceId(null);
                }}
                onSave={handleSaveContact}
                editingRecord={editingContact}
                establishmentLocationId={currentEstablishmentId || undefined}
                placeId={currentPlaceId || undefined}
                loading={contactsLoading}
            />

            {/* Place Modal */}
            <PlaceModal
                visible={placeModalVisible}
                onCancel={() => {
                    setPlaceModalVisible(false);
                    setEditingPlace(null);
                    setCurrentEstablishmentId(null);
                }}
                onSave={handleSavePlace}
                editingRecord={editingPlace}
                establishmentLocationId={currentEstablishmentId || ''}
                loading={placesLoading}
            />

            {/* Establishment-Location Delete Modal — pre-checks claim count and swaps
                into a "Disable instead" prompt when claims still reference the row. */}
            <Modal
                title={
                    estDeleteClaimCount && estDeleteClaimCount > 0 ? (
                        <span>⚠️ Cannot Delete Establishment</span>
                    ) : (
                        <span style={{ color: '#ff4d4f' }}>
                            <DeleteOutlined /> Delete Establishment
                        </span>
                    )
                }
                open={!!estDeleteTarget}
                onCancel={closeEstablishmentDelete}
                footer={
                    estDeleteChecking ? [
                        <Button key="cancel" onClick={closeEstablishmentDelete}>Cancel</Button>
                    ] : estDeleteClaimCount === null ? [
                        <Button key="cancel" onClick={closeEstablishmentDelete}>Close</Button>
                    ] : estDeleteClaimCount > 0 ? [
                        <Button key="cancel" onClick={closeEstablishmentDelete}>Cancel</Button>,
                        estDeleteTarget?.status === ActivationStatus.Active && (
                            <Button key="disable" type="primary" onClick={disableEstablishmentInstead}>
                                Disable Instead
                            </Button>
                        )
                    ].filter(Boolean) : [
                        <Button key="cancel" onClick={closeEstablishmentDelete}>Cancel</Button>,
                        <Button key="delete" type="primary" danger onClick={confirmEstablishmentDelete}>
                            Yes, Delete
                        </Button>
                    ]
                }
                width={520}
            >
                {estDeleteChecking ? (
                    <Space style={{ width: '100%', justifyContent: 'center', padding: '24px 0' }}>
                        <Spin /> <Text type="secondary">Checking referencing claims…</Text>
                    </Space>
                ) : estDeleteClaimCount === null ? (
                    <Text type="warning">Could not verify referencing claims. Please try again.</Text>
                ) : estDeleteClaimCount > 0 ? (
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <Text>
                            <Text strong>{estDeleteTarget?.name}</Text> is referenced by{' '}
                            <Text strong>{estDeleteClaimCount}</Text> existing claim
                            {estDeleteClaimCount === 1 ? '' : 's'}.
                        </Text>
                        <Text type="secondary">
                            Deleting it would make those claims render the raw ID instead of the name.
                            {estDeleteTarget?.status === ActivationStatus.Active
                                ? ' Disable it instead — it will be hidden from new-claim dropdowns while existing claims keep showing the name. You can delete it later, once no active claims reference it.'
                                : ' It is already disabled. It can be deleted once no active claims reference it.'}
                        </Text>
                    </Space>
                ) : (
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <Text>
                            Delete <Text strong>{estDeleteTarget?.name}</Text>? No claims currently reference it.
                        </Text>
                        <Text type="secondary">This action cannot be undone.</Text>
                    </Space>
                )}
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                title={
                    <span style={{ color: '#ff4d4f' }}>
                        <DeleteOutlined /> Delete Establishment Type
                    </span>
                }
                open={deleteConfirmModalVisible}
                onCancel={handleCancelDelete}
                footer={[
                    <Button key="cancel" onClick={handleCancelDelete}>
                        Cancel
                    </Button>,
                    <Button
                        key="delete"
                        type="primary"
                        danger
                        disabled={deleteConfirmText !== "DELETE"}
                        onClick={handleConfirmDelete}
                    >
                        Delete Permanently
                    </Button>
                ]}
                width={600}
            >
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                    <div>
                        <Text strong style={{ fontSize: 16 }}>
                            You are about to delete: <Text type="danger">{typeToDelete?.name}</Text>
                        </Text>
                    </div>

                    <div style={{
                        padding: 16,
                        background: '#fff2e8',
                        border: '1px solid #ffbb96',
                        borderRadius: 4
                    }}>
                        <Text strong style={{ color: '#ff4d4f', display: 'block', marginBottom: 12 }}>
                            ⚠️ Warning: This action cannot be undone!
                        </Text>
                        <Text style={{ display: 'block', marginBottom: 8 }}>
                            Deleting this establishment type will permanently remove:
                        </Text>
                        <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                            <li><Text strong>All establishments</Text> under this type</li>
                            <li><Text strong>All places</Text> within those establishments</li>
                            <li><Text strong>All contacts</Text> (address book entries) for establishments and places</li>
                            <li><Text strong>All questions</Text> configured for this type</li>
                        </ul>
                    </div>

                    <div>
                        <Text style={{ display: 'block', marginBottom: 8 }}>
                            To confirm deletion, please type <Text strong code>DELETE</Text> below:
                        </Text>
                        <Input
                            placeholder="Type DELETE to confirm"
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                            status={deleteConfirmText && deleteConfirmText !== "DELETE" ? "error" : undefined}
                            size="large"
                        />
                        {deleteConfirmText && deleteConfirmText !== "DELETE" && (
                            <Text type="danger" style={{ display: 'block', marginTop: 8 }}>
                                Please type exactly "DELETE" (in capital letters)
                            </Text>
                        )}
                    </div>
                </Space>
            </Modal>
        </div>
    );
};

export default ImprovedEstablishmentTypePage;
